import React, { useState, useEffect, useCallback } from 'react';
import {
  FlaskConical, Play, CheckCircle2, AlertTriangle, XCircle,
  Loader2, Star, Shield, Brain, Heart, Eye, Users, Zap,
  ChevronDown, ChevronUp, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { casesService } from '../services';
import { AppLayout } from '../components/AppLayout';
import { toast } from 'sonner';

import { EMPTY_STATES } from '../design-system/content';
import { getScoreBandClass } from '../design-system/tokens';
import { toCaseVM } from '../design-system/viewmodels';
import { StatusBadge, EmptyState, PageHeader } from '../components/shared';
import { OptimizeProgressPanel } from '../components/workspace/OptimizeProgressPanel';

const DIMENSION_TABS = [
  { key: 'agency',               label: 'Agency',      icon: Users,  color: '#3b82f6', desc: 'User empowerment' },
  { key: 'clarity',              label: 'Clarity',     icon: Eye,    color: '#8b5cf6', desc: 'Clear communication' },
  { key: 'emotional_regulation', label: 'Emotional',   icon: Heart,  color: '#ef4444', desc: 'Emotional support' },
  { key: 'cognitive_load',       label: 'Cognitive',   icon: Brain,  color: '#f59e0b', desc: 'Information density' },
  { key: 'safety',               label: 'Safety',      icon: Shield, color: '#10b981', desc: 'Risk & disclaimers' },
  { key: 'dependency',           label: 'Dependency',  icon: Zap,    color: '#ec4899', desc: 'AI reliance' },
];

const QUALITY_LABELS = ['Poor', 'Weak', 'Moderate', 'Strong'];
// Elegant translucent tints — one layer of color on glass, no loud backgrounds.
// The base `.liquid-card` brings the glass material; these classes only add
// a colored left-edge glow + a very faint colored tint, so quality is readable
// at a glance without competing with the title/excerpt.
const QUALITY_COLORS = [
  'border-l-[3px] border-l-[rgba(255,90,90,0.45)] shadow-[inset_0_0_0_1px_rgba(255,90,90,0.08)] before:absolute before:inset-y-0 before:left-0 before:w-10 before:bg-gradient-to-r before:from-[rgba(255,90,90,0.10)] before:to-transparent before:pointer-events-none',
  'border-l-[3px] border-l-[rgba(255,160,60,0.45)] shadow-[inset_0_0_0_1px_rgba(255,160,60,0.08)] before:absolute before:inset-y-0 before:left-0 before:w-10 before:bg-gradient-to-r before:from-[rgba(255,160,60,0.10)] before:to-transparent before:pointer-events-none',
  'border-l-[3px] border-l-[rgba(245,200,60,0.45)] shadow-[inset_0_0_0_1px_rgba(245,200,60,0.08)] before:absolute before:inset-y-0 before:left-0 before:w-10 before:bg-gradient-to-r before:from-[rgba(245,200,60,0.10)] before:to-transparent before:pointer-events-none',
  'border-l-[3px] border-l-[rgba(72,220,150,0.50)] shadow-[inset_0_0_0_1px_rgba(72,220,150,0.10)] before:absolute before:inset-y-0 before:left-0 before:w-10 before:bg-gradient-to-r before:from-[rgba(72,220,150,0.12)] before:to-transparent before:pointer-events-none',
];


// ── Inline expanded result with before/after comparison ──
function InlineResult({ result, optimizeState, optimizedText, optResultData }) {
  if (!result) return null;
  const dims = result.dimensions || [];
  const expected = result.expected_dimension_ranges || {};
  const expectedHq = result.expected_hq_range;
  const hqInRange = expectedHq
    ? result.hq_score >= (expectedHq.min || 0) && result.hq_score <= (expectedHq.max || 100)
    : true;

  const afterHq = optResultData?.optimized_hq_score ?? optResultData?.after_scores?.hq_score;
  const deltaOverall = optResultData?.delta_overall;
  const deltaDims = optResultData?.delta_dimensions;
  const afterDimScores = optResultData?.after_scores?.dimension_scores;
  const hasOptScores = optimizeState === 'done' && afterHq != null;

  return (
    <div className="space-y-2 mt-2 pt-2 border-t border-white/10" data-testid="inline-result">

      {/* ── HQ Score: Original + Expected | Delta | Improved ── */}
      <div className="flex items-center gap-3">
        {/* Original HQ + Expected (grouped on the left) */}
        <div>
          <p className="text-[8px] text-[var(--msml-text-muted)] uppercase">Original HQ</p>
          <div className="flex items-baseline gap-2">
            <p className={`text-xl font-bold ${getScoreBandClass(result.hq_score)}`}>
              {result.hq_score?.toFixed(1)}
            </p>
            {expectedHq && (
              <span className="flex items-center gap-1">
                <span className="text-[9px] text-[var(--msml-text-muted)]">/ expected {expectedHq.min}–{expectedHq.max}</span>
                {hqInRange ? (
                  <CheckCircle2 className="w-3 h-3 text-[var(--msml-success)]" />
                ) : (
                  <AlertTriangle className="w-3 h-3 text-[var(--msml-warning)]" />
                )}
              </span>
            )}
          </div>
        </div>

        {/* Delta + Improved HQ (right side) */}
        {hasOptScores && (
          <>
            <div className="flex flex-col items-center px-1 ml-auto">
              <span className="text-[8px] text-[var(--msml-text-muted)]">delta</span>
              <span className={`text-sm font-bold ${deltaOverall > 0 ? 'text-[var(--msml-success)]' : deltaOverall < 0 ? 'text-[var(--msml-danger)]' : 'text-[var(--msml-text-muted)]'}`}>
                {deltaOverall > 0 ? '+' : ''}{deltaOverall?.toFixed(1)}
              </span>
            </div>
            <div className="text-right">
              <p className="text-[8px] text-[#60a5fa] uppercase">Improved HQ</p>
              <p className={`text-xl font-bold ${getScoreBandClass(afterHq)}`}>
                {afterHq?.toFixed(1)}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Flags */}
      {(result.caps_applied?.length > 0 || result.flags?.high_risk || result.flags?.high_dependency || result.flags?.overload) && (
        <div className="flex flex-wrap gap-1">
          {result.flags?.high_risk && <StatusBadge label="High Risk" variant="error" />}
          {result.flags?.high_dependency && <StatusBadge label="High Dependency" variant="warning" />}
          {result.flags?.overload && <StatusBadge label="Overload" variant="warning" />}
        </div>
      )}

      {/* ── Dimension scores: Before → After with delta ── */}
      <div className="grid grid-cols-3 gap-1">
        {dims.map(d => {
          const exp = expected[d.key];
          const inRange = exp ? d.score >= (exp.min || 0) && d.score <= (exp.max || 100) : true;
          const dd = deltaDims?.[d.key];
          const afterScore = afterDimScores?.[d.key] ?? dd?.after;
          const dimDelta = dd?.delta;

          return (
            <div key={d.key} className="py-1 px-1 rounded bg-white/3">
              <p className="text-[7px] text-[var(--msml-text-muted)] uppercase truncate text-center">{d.short || d.label}</p>
              <div className="flex items-center justify-center gap-1">
                <span className={`text-xs font-bold ${getScoreBandClass(d.score)}`}>{d.score?.toFixed(0)}</span>
                {hasOptScores && afterScore != null && (
                  <>
                    <span className="text-[7px] text-[var(--msml-text-muted)]">&rarr;</span>
                    <span className={`text-xs font-bold ${getScoreBandClass(afterScore)}`}>{afterScore?.toFixed(0)}</span>
                  </>
                )}
              </div>
              {hasOptScores && dimDelta != null && (
                <p className={`text-[7px] text-center font-medium ${dimDelta > 0 ? 'text-[var(--msml-success)]' : dimDelta < 0 ? 'text-[var(--msml-danger)]' : 'text-[var(--msml-text-muted)]'}`}>
                  {dimDelta > 0 ? '+' : ''}{dimDelta?.toFixed(0)}
                </p>
              )}
              {!hasOptScores && exp && (
                <p className={`text-[7px] text-center ${inRange ? 'text-[var(--msml-success)]' : 'text-[var(--msml-warning)]'}`}>
                  {inRange ? 'pass' : 'miss'}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Failure modes */}
      {result.expected_failure_modes?.length > 0 && (
        <div className="space-y-0.5">
          <p className="text-[8px] text-[var(--msml-text-muted)] uppercase">Failure Modes</p>
          {result.expected_failure_modes.map((fm, i) => {
            const label = typeof fm === 'string' ? fm.replace(/_/g, ' ') : (fm.description || fm.mode || fm.dimension);
            return (
              <div key={i} className="flex items-center gap-1 text-[10px]">
                <XCircle className="w-2.5 h-2.5 text-[var(--msml-danger)] flex-shrink-0" />
                <span className="text-[var(--msml-text-secondary)] capitalize">{label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Optimized text ── */}
      <div className="pt-1 border-t border-white/10">
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles className="w-3 h-3 text-[#60a5fa]" />
          <p className="text-[9px] text-[#60a5fa] uppercase font-semibold tracking-wide">
            Improved for Human Benefit
          </p>
        </div>
        {optimizeState === 'done' && optimizedText && (
          <div className="rounded-md bg-blue-500/5 border border-blue-500/15 p-2" data-testid="optimized-text">
            <p className="text-[10px] text-[var(--msml-text-secondary)] leading-relaxed whitespace-pre-line">
              {optimizedText}
            </p>
          </div>
        )}
        {optimizeState === 'error' && (
          <p className="text-[10px] text-[var(--msml-text-muted)] italic">
            Optimization unavailable — scoring completed successfully above.
          </p>
        )}
      </div>
    </div>
  );
}


export default function CasesPage() {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('agency');
  const [runningCase, setRunningCase] = useState(null);
  // Per-case: { result, optimizeState, optimizedText, phase, optProgress }
  const [caseResults, setCaseResults] = useState({});
  const [expanded, setExpanded] = useState({});

  useEffect(() => { loadCases(); }, []);

  const loadCases = async () => {
    setLoading(true);
    try {
      const data = await casesService.getSamples();
      setSamples((data.items || []).map(toCaseVM));
    } catch (err) {
      console.error('Failed to load cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = useCallback((caseId) => {
    setExpanded(prev => ({ ...prev, [caseId]: !prev[caseId] }));
  }, []);

  const handleRunCase = async (caseItem) => {
    const cid = caseItem.caseId;
    setRunningCase(cid);
    setExpanded(prev => ({ ...prev, [cid]: true }));

    // Initialize progress state
    setCaseResults(prev => ({
      ...prev,
      [cid]: { result: null, optimizeState: null, optimizedText: null, phase: 'scoring', optProgress: null },
    }));

    try {
      // Step 1: Score the case
      const data = await casesService.runCase(cid);

      setCaseResults(prev => ({
        ...prev,
        [cid]: { ...prev[cid], result: data, phase: 'scored' },
      }));
      toast.success(`Case scored: HQ ${data.hq_score?.toFixed(1)}`);

      // Step 2: Start optimization
      const analysisId = data.analysis_id || data.run_id;
      if (analysisId) {
        setCaseResults(prev => ({
          ...prev,
          [cid]: { ...prev[cid], phase: 'optimizing', optimizeState: 'loading' },
        }));

        try {
          await fetch(`/api/optimize/start/${analysisId}`, {
            method: 'POST',
            credentials: 'include',
          });

          // Poll for progress + completion
          let optResult = null;
          for (let i = 0; i < 100; i++) {
            await new Promise(r => setTimeout(r, 700));
            const resp = await fetch(`/api/optimize/progress/${analysisId}`, { credentials: 'include' });
            if (!resp.ok) continue;
            const progress = await resp.json();

            // Update progress in state for live UI updates
            setCaseResults(prev => ({
              ...prev,
              [cid]: { ...prev[cid], optProgress: progress },
            }));

            if (progress.done && progress.result) {
              optResult = progress.result;
              break;
            }
            if (progress.done && progress.title === 'Error') break;
          }

          setCaseResults(prev => ({
            ...prev,
            [cid]: {
              ...prev[cid],
              phase: 'complete',
              optimizeState: optResult ? 'done' : 'error',
              optimizedText: optResult?.optimized_text || null,
              optResultData: optResult || null,
            },
          }));
        } catch {
          setCaseResults(prev => ({
            ...prev,
            [cid]: { ...prev[cid], phase: 'complete', optimizeState: 'error' },
          }));
        }
      } else {
        setCaseResults(prev => ({
          ...prev,
          [cid]: { ...prev[cid], phase: 'complete', optimizeState: 'error' },
        }));
      }
    } catch (err) {
      toast.error('Failed to run case');
      setCaseResults(prev => ({
        ...prev,
        [cid]: { ...prev[cid], phase: null },
      }));
    } finally {
      setRunningCase(null);
    }
  };

  const casesByDim = {};
  DIMENSION_TABS.forEach(t => { casesByDim[t.key] = []; });
  samples.forEach(c => {
    if (casesByDim[c.category]) casesByDim[c.category].push(c);
  });

  const activeDim = DIMENSION_TABS.find(t => t.key === activeTab) || DIMENSION_TABS[0];
  const activeCases = casesByDim[activeTab] || [];

  return (
    <AppLayout activeNav="cases">
      <div className="p-4 lg:p-6 h-full flex flex-col" data-testid="cases-page">
        <PageHeader
          icon={<FlaskConical />}
          title="Validation Cases"
          subtitle={`${samples.length} curated cases across 6 dimensions — run any case to verify scoring and optimization against expected ranges.`}
        />

        {loading ? (
          <div className="flex justify-center py-16"><span className="msml-spinner" /></div>
        ) : samples.length === 0 ? (
          <EmptyState
            icon={<FlaskConical className="w-12 h-12" />}
            title={EMPTY_STATES.cases?.title || 'No cases'}
            body={EMPTY_STATES.cases?.body || 'No validation cases available.'}
          />
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Dimension Tabs — glass segmented rail */}
            <div className="liquid-segmented mb-4 overflow-x-auto flex-shrink-0 self-start max-w-full" data-testid="dimension-tabs">
              {DIMENSION_TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.key;
                const count = (casesByDim[tab.key] || []).length;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    data-testid={`tab-${tab.key}`}
                    data-active={isActive ? 'true' : 'false'}
                    className="liquid-segmented__item whitespace-nowrap"
                    style={isActive ? { color: tab.color } : undefined}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {tab.label}
                    <span className="text-[10px] px-1.5 py-0 rounded-full bg-[var(--glass-stroke)]">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Dimension description */}
            <div className="flex items-center gap-2 mb-3 px-1 flex-shrink-0" data-testid="dimension-desc">
              <div className="w-1 h-4 rounded-full" style={{ background: activeDim.color }} />
              <span className="text-[11px] text-[var(--msml-text-muted)]">
                <span className="font-medium text-[var(--msml-text-secondary)]">{activeDim.label}</span>
                {' — '}{activeDim.desc} — Cases progress from poor to strong
              </span>
            </div>

            {/* 4 Case Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 flex-1 min-h-0 auto-rows-min" data-testid="case-grid">
              {activeCases.map((c, idx) => {
                const cid = c.caseId;
                const isRunning = runningCase === cid;
                const qualityIdx = Math.min(idx, 3);
                const cr = caseResults[cid];
                const hasResult = !!cr?.result;
                const isExpanded = expanded[cid] && (hasResult || cr?.phase);
                const isInProgress = cr?.phase && cr.phase !== 'complete' && !hasResult;
                const showProgress = cr?.phase && cr.phase !== 'complete';

                return (
                  <div
                    key={cid}
                    data-testid={`case-card-${cid}`}
                    className={`liquid-card liquid-lift relative overflow-hidden !p-3 flex flex-col transition-all
                      ${QUALITY_COLORS[qualityIdx]}
                      ${c.featured ? 'ring-1 ring-amber-400/40' : ''}
                      ${isExpanded ? 'xl:col-span-2 row-span-2' : ''}`}
                  >
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-1.5 relative z-10">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] uppercase tracking-widest font-bold text-ink-muted">
                          {QUALITY_LABELS[qualityIdx]}
                        </span>
                        {c.featured && (
                          <span className="flex items-center gap-0.5 text-[9px] text-amber-400 font-medium" data-testid="featured-badge">
                            <Star className="w-2.5 h-2.5 fill-amber-400" /> Showcase
                          </span>
                        )}
                      </div>
                      {hasResult && (
                        <button
                          onClick={() => toggleExpanded(cid)}
                          className="p-0.5 rounded hover:bg-white/10 transition-colors"
                          data-testid={`toggle-${cid}`}
                        >
                          {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-ink-muted" /> : <ChevronDown className="w-3.5 h-3.5 text-ink-muted" />}
                        </button>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-xs font-semibold text-ink leading-tight mb-1 line-clamp-1 relative z-10" title={c.title}>
                      {c.title}
                    </h3>

                    {/* Text excerpt — hide when expanded or in progress */}
                    {!isExpanded && !showProgress && (
                      <p className="text-[10px] text-ink-2 leading-relaxed line-clamp-3 mb-2 flex-1 italic relative z-10">
                        "{c.originalText}"
                      </p>
                    )}

                    {/* ── PROGRESS INDICATOR (during scoring + optimization) ── */}
                    {showProgress && (
                      <OptimizeProgressPanel
                        progress={cr?.optProgress || { stage: 1, percent: 5, title: 'Preparing input', desc: 'Extracting signals and computing score.' }}
                        compact
                      />
                    )}

                    {/* Expected HQ + Notes — hide when expanded or in progress */}
                    {!isExpanded && !showProgress && (
                      <div className="space-y-1 mb-2">
                        {c.expectedHqRange && (
                          <p className="text-[10px] text-[var(--msml-text-muted)]">
                            Expected HQ: <span className="font-medium text-[var(--msml-text-secondary)]">{c.expectedHqRange.min}–{c.expectedHqRange.max}</span>
                          </p>
                        )}
                        {c.notes && (
                          <p className="text-[9px] text-[var(--msml-text-muted)] line-clamp-1" title={c.notes}>{c.notes}</p>
                        )}
                      </div>
                    )}

                    {/* Expanded inline result (after complete) */}
                    {isExpanded && hasResult && !showProgress && (
                      <InlineResult result={cr.result} optimizeState={cr.optimizeState} optimizedText={cr.optimizedText} optResultData={cr.optResultData} />
                    )}

                    {/* Collapsed result summary */}
                    {hasResult && !isExpanded && !showProgress && (
                      <div className="flex items-center gap-2 mb-2 py-1 px-2 rounded bg-white/5 text-[10px]" data-testid={`result-summary-${cid}`}>
                        <span className={`font-bold ${getScoreBandClass(cr.result.hq_score)}`}>
                          HQ {cr.result.hq_score?.toFixed(1)}
                        </span>
                        {cr.optResultData?.delta_overall != null && (
                          <>
                            <span className="text-[var(--msml-text-muted)]">&rarr;</span>
                            <span className={`font-bold ${getScoreBandClass(cr.optResultData.optimized_hq_score)}`}>
                              {cr.optResultData.optimized_hq_score?.toFixed(1)}
                            </span>
                            <span className={`font-medium ${cr.optResultData.delta_overall > 0 ? 'text-[var(--msml-success)]' : 'text-[var(--msml-danger)]'}`}>
                              ({cr.optResultData.delta_overall > 0 ? '+' : ''}{cr.optResultData.delta_overall?.toFixed(1)})
                            </span>
                          </>
                        )}
                        {cr.optimizedText && !cr.optResultData?.delta_overall && (
                          <>
                            <span className="text-[var(--msml-text-muted)]">|</span>
                            <span className="text-[#60a5fa] flex items-center gap-0.5">
                              <Sparkles className="w-2.5 h-2.5" /> Optimized
                            </span>
                          </>
                        )}
                      </div>
                    )}

                    {/* Action button */}
                    {!hasResult && !showProgress ? (
                      <Button
                        size="sm"
                        onClick={() => handleRunCase(c)}
                        disabled={!!runningCase}
                        className="w-full text-[11px] h-7 mt-auto"
                        style={{ background: runningCase ? undefined : activeDim.color, borderColor: activeDim.color }}
                        data-testid={`run-case-${cid}`}
                      >
                        <Play className="w-3 h-3 mr-1" /> Run Case
                      </Button>
                    ) : hasResult && !isExpanded && !showProgress ? (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => toggleExpanded(cid)}
                        className="w-full text-[11px] h-7 mt-auto border-white/10 text-[var(--msml-text-secondary)]"
                        data-testid={`expand-${cid}`}
                      >
                        <ChevronDown className="w-3 h-3 mr-1" /> View Results
                      </Button>
                    ) : isExpanded && hasResult && !showProgress ? (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => toggleExpanded(cid)}
                        className="w-full text-[11px] h-7 mt-auto border-white/10 text-[var(--msml-text-secondary)]"
                        data-testid={`collapse-${cid}`}
                      >
                        <ChevronUp className="w-3 h-3 mr-1" /> Collapse
                      </Button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
